import { ampli } from '@/ampli';
import { useEffect, useMemo, useReducer, useRef } from 'react';
import { toast } from 'sonner';

import { NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID } from '@/constants/addresses';
import { CHAIN_IDS } from '@/constants/chain';
import { CHAIN_DEPRECATION_END_DATE, DEPRECATED_CHAIN_IDS } from '@/constants/chainDeprecation';

import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useWalletPositions } from '@/hooks/useWalletPositions';

import { chainDeprecationSelectors, useAppStore } from '@/store/app';

import { tradeRoutePathname } from '@/utils/url';
import type { TokenBalance } from '@/utils/zerion/types';

import { ChainDeprecationToast, type DeprecatedChainBalance } from './ChainDeprecationToast';

const TOAST_ID = 'chain-deprecation';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
const MIN_CHAIN_BALANCE_USD = 0.01;

const bridgeHref = (token: TokenBalance) => ({
  pathname: tradeRoutePathname('swap'),
  query: {
    sellChain: String(token.chainId),
    sellAddress: token.tokenAddress,
    buyChain: String(CHAIN_IDS.MAINNET),
    buyAddress: NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID[CHAIN_IDS.MAINNET],
  },
});

function summarizeBalances(allTokens: TokenBalance[]): {
  chains: DeprecatedChainBalance[];
  totalUsd: number;
  primaryChain: DeprecatedChainBalance;
} | null {
  // `allTokens` is sorted by USD value, descending, so the first token seen on a
  // chain is its top asset, and the first chain seen holds the top asset overall.
  const byChain = new Map<number, { balanceUsd: number; topToken: TokenBalance }>();
  for (const token of allTokens) {
    const entry = byChain.get(token.chainId);
    if (entry) entry.balanceUsd += token.balanceUsd ?? 0;
    else byChain.set(token.chainId, { balanceUsd: token.balanceUsd ?? 0, topToken: token });
  }

  const held = [...byChain]
    .filter(
      ([chainId, { balanceUsd }]) =>
        DEPRECATED_CHAIN_IDS.includes(chainId) && balanceUsd >= MIN_CHAIN_BALANCE_USD,
    )
    .map(([chainId, { balanceUsd, topToken }]) => ({
      chainId,
      balanceUsd,
      bridgeHref: bridgeHref(topToken),
    }));
  if (held.length === 0) return null;

  return {
    chains: DEPRECATED_CHAIN_IDS.flatMap((id) => held.filter(({ chainId }) => chainId === id)),
    totalUsd: held.reduce((sum, { balanceUsd }) => sum + balanceUsd, 0),
    primaryChain: held[0],
  };
}

export function useChainDeprecationPrompt() {
  const { ethereumWallet, isConnected } = useMatchaWallets();
  const evmAddress = ethereumWallet?.address?.toLowerCase();
  const { chainDeprecationDismissedUntilByWallet, setChainDeprecationDismissedUntil } =
    useAppStore(chainDeprecationSelectors);
  const chainDeprecationDismissedUntil = evmAddress
    ? (chainDeprecationDismissedUntilByWallet[evmAddress] ?? 0)
    : 0;
  const [, recheckDismissal] = useReducer((tick: number) => tick + 1, 0);

  useEffect(() => {
    const delay = chainDeprecationDismissedUntil - Date.now();
    if (delay <= 0) return;

    // The disabled positions query cannot trigger a render when dismissal expires.
    const timeout = setTimeout(recheckDismissal, delay);
    return () => clearTimeout(timeout);
  }, [chainDeprecationDismissedUntil]);

  const now = Date.now();
  const enabled =
    DEPRECATED_CHAIN_IDS.length > 0 &&
    now < CHAIN_DEPRECATION_END_DATE.getTime() &&
    isConnected &&
    !!evmAddress &&
    now >= chainDeprecationDismissedUntil;

  const { allTokens } = useWalletPositions({
    evmAddress,
    chainIds: DEPRECATED_CHAIN_IDS,
    enabled,
  });

  const summary = useMemo(() => summarizeBalances(allTokens), [allTokens]);

  const shownRef = useRef(false);

  useEffect(() => {
    if (!enabled || !evmAddress) return;
    if (!summary) {
      shownRef.current = false;
      toast.dismiss(TOAST_ID);
      return;
    }

    const { chains, totalUsd, primaryChain } = summary;
    const chainIds = chains.map(({ chainId }) => chainId);

    // Swiping right fires both the container's `onClose` and Sonner's `onDismiss`,
    // so only the first call is tracked and persisted.
    const dismiss = (track: () => void) => {
      if (!shownRef.current) return;
      shownRef.current = false;
      track();
      setChainDeprecationDismissedUntil(evmAddress, Date.now() + DISMISS_DURATION_MS);
      toast.dismiss(TOAST_ID);
    };
    const close = () =>
      dismiss(() => ampli.chainDeprecationToastDismissed({ chain_ids: chainIds }));

    if (!shownRef.current) {
      shownRef.current = true;
      ampli.chainDeprecationToastDisplayed({ chain_ids: chainIds });
    }

    // A fixed id makes refetches update the toast in place.
    toast.custom(
      () => (
        <ChainDeprecationToast
          chains={chains}
          totalUsd={totalUsd}
          primaryChain={primaryChain}
          onBridgeClick={(chainId) =>
            dismiss(() =>
              ampli.chainDeprecationToastClicked({
                chain_id: chainId,
                total_balance_usd: totalUsd,
              }),
            )
          }
          onClose={close}
        />
      ),
      {
        id: TOAST_ID,
        duration: Infinity,
        unstyled: true,
        // Sonner's swipe calls this, not `onClose`. `delete` marks our own
        // `toast.dismiss` calls, which hide the toast without counting as a user dismissal.
        onDismiss: ({ delete: dismissedByApp }) => {
          if (!dismissedByApp) close();
        },
      },
    );
  }, [enabled, evmAddress, summary, setChainDeprecationDismissedUntil]);

  useEffect(() => {
    if (!enabled) return;
    return () => {
      shownRef.current = false;
      toast.dismiss(TOAST_ID);
    };
  }, [enabled, evmAddress]);
}
